import {
    useState, useRef, useCallback, useEffect,
} from 'react';

const LOADING_DELAY_MS = 333;
const LOADING_TIMEOUT_MS = 30000;

/**
 * Hook that provides delayed loading state.
 * Loading indicator only appears after a short delay.
 * This prevents flickering for fast operations while still showing
 * feedback for slow calculations.
 */
export const useDelayedLoading = () => {
    const [isLoading, setIsLoading] = useState(false);
    const delayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const fallbackTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearTimeouts = useCallback(() => {
        if (delayTimeoutRef.current) {
            clearTimeout(delayTimeoutRef.current);
            delayTimeoutRef.current = null;
        }
        if (fallbackTimeoutRef.current) {
            clearTimeout(fallbackTimeoutRef.current);
            fallbackTimeoutRef.current = null;
        }
    }, []);

    const startLoading = useCallback(() => {
        clearTimeouts();

        delayTimeoutRef.current = setTimeout(() => {
            delayTimeoutRef.current = null;
            setIsLoading(true);
        }, LOADING_DELAY_MS);

        fallbackTimeoutRef.current = setTimeout(() => {
            fallbackTimeoutRef.current = null;
            setIsLoading(false);
        }, LOADING_TIMEOUT_MS);
    }, [clearTimeouts]);

    const stopLoading = useCallback(() => {
        clearTimeouts();
        setIsLoading(false);
    }, [clearTimeouts]);

    useEffect(() => () => {
        clearTimeouts();
    }, [clearTimeouts]);

    return { isLoading, startLoading, stopLoading };
};

export default useDelayedLoading;
